/**
 * Alias des polices du site aux métriques corrigées (lib/fontMetrics.ts) : partie navigateur.
 *
 * Pour chaque pile de la v2 (--mmv2-font, --mmv2-font-display) :
 *   1. on cherche la famille qui peint réellement le texte : la première de la pile déclarée par un @font-face lisible
 *      (feuille du même domaine), ou installée sur l'appareil, ou générique (alors on s'arrête : rien à corriger) ;
 *   2. pour une police web, on attend son chargement, on mesure à l'encre (canvas) ascent, descent et hauteur de
 *      capitale, et, si son texte n'est pas centré dans sa boîte, on déclare un alias « mmv2 <famille> » : MÊMES
 *      fichiers (cache HTTP), mêmes graisses, styles et plages, avec ascent-override / descent-override ;
 *   3. la pile devient « 'mmv2 <famille>', <pile d'origine> » : tant que l'alias charge (font-display: swap), ou s'il
 *      échoue, le texte reste dans la police d'origine, au pire 1 à 2 px plus haut, comme avant.
 * L'alias n'a pas d'interligne propre (line-gap-override: 0%) : `line-height: normal` y vaut la zone de contenu.
 *
 * Ne lève jamais : en cas de doute (feuille d'un autre domaine, police absente, API manquante), la pile est rendue
 * telle quelle. Appelée seulement quand la v2 est active (mountV2) ; seul effet global : des FontFace au nom préfixé
 * « mmv2 », sans effet sur la page autour de l'app.
 */
import {
  absolutizeFontSrc,
  aliasFamilyName,
  centeredOverrides,
  coversBasicLatin,
  isGenericFamily,
  parseFontStack,
  stackWithAlias,
  textNudge,
  type FontMeasure,
} from '../lib/fontMetrics';

/** Taille de mesure : grande, pour que l'arrondi des métriques au pixel soit négligeable (0,1 %). */
const MEASURE_PX = 1000;

/** Délai maximal d'attente d'une police d'origine avant de renoncer (la pile reste alors inchangée). */
const LOAD_TIMEOUT_MS = 4000;

const DESCRIPTORS: Array<[keyof FontFaceDescriptors, string]> = [
  ['style', 'font-style'],
  ['weight', 'font-weight'],
  ['stretch', 'font-stretch'],
  ['unicodeRange', 'unicode-range'],
  ['featureSettings', 'font-feature-settings'],
];

const unquote = (value: string) => value.trim().replace(/^(['"])(.*)\1$/, '$2').trim();

/** @font-face lisibles (feuilles du même domaine, @import, @media, @supports, @layer compris) d'une famille. */
export function findFontFaceRules(family: string, doc: Document = document): CSSFontFaceRule[] {
  const wanted = family.toLowerCase();
  const found: CSSFontFaceRule[] = [];
  const visit = (rules: CSSRuleList | undefined, depth: number) => {
    if (!rules || depth > 6) return;
    for (const rule of Array.from(rules)) {
      if (rule instanceof CSSFontFaceRule) {
        // Règle partielle (plage sans le latin de base) : elle ne peint pas le texte de l'app.
        if (
          unquote(rule.style.getPropertyValue('font-family')).toLowerCase() === wanted &&
          coversBasicLatin(rule.style.getPropertyValue('unicode-range'))
        ) {
          found.push(rule);
        }
      } else if (rule instanceof CSSImportRule) {
        visit(safeRules(rule.styleSheet), depth + 1);
      } else if ('cssRules' in rule) {
        visit((rule as CSSGroupingRule).cssRules, depth + 1);
      }
    }
  };
  Array.from(doc.styleSheets).forEach((sheet) => visit(safeRules(sheet), 0));
  return found;
}

function safeRules(sheet: CSSStyleSheet | null): CSSRuleList | undefined {
  try {
    return sheet?.cssRules;
  } catch {
    return undefined; // feuille d'un autre domaine : illisible
  }
}

/** Police installée sur l'appareil ? (largeur d'un texte comparée à deux replis génériques) */
function isLocalFontAvailable(family: string, ctx: CanvasRenderingContext2D): boolean {
  const sample = 'mmmmmmmmmmlli1WQ@';
  return ['monospace', 'serif'].some((generic) => {
    ctx.font = `72px ${generic}`;
    const base = ctx.measureText(sample).width;
    ctx.font = `72px "${family}", ${generic}`;
    return ctx.measureText(sample).width !== base;
  });
}

function measure(family: string, weight: string, ctx: CanvasRenderingContext2D): FontMeasure | null {
  ctx.font = `normal ${weight} ${MEASURE_PX}px "${family}"`;
  const m = ctx.measureText('H');
  if (!('fontBoundingBoxAscent' in m) || !Number.isFinite(m.fontBoundingBoxAscent)) return null;
  return {
    ascent: m.fontBoundingBoxAscent / MEASURE_PX,
    descent: m.fontBoundingBoxDescent / MEASURE_PX,
    capHeight: m.actualBoundingBoxAscent / MEASURE_PX,
  };
}

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T | null> =>
  Promise.race([promise, new Promise<null>((resolve) => window.setTimeout(() => resolve(null), ms))]);

/** Graisse de référence de la mesure : 400 si la famille la déclare, sinon la première déclarée. */
function referenceWeight(rules: CSSFontFaceRule[]): string {
  const weights = rules.map((rule) => rule.style.getPropertyValue('font-weight').trim().split(/\s+/)[0] || '400');
  return weights.find((w) => w === '400' || w === 'normal') ?? weights[0] ?? '400';
}

const aliasCache = new Map<string, Promise<string | null>>();

/** Écart admis entre deux valeurs d'ascent-override / descent-override (points de pourcentage). */
const OVERRIDE_TOLERANCE = 0.01;

const samePercent = (a: string | undefined, b: string) =>
  Math.abs(parseFloat(a ?? '') - parseFloat(b)) <= OVERRIDE_TOLERANCE;

/**
 * Alias déjà déclaré avec ces métriques (script en ligne du squelette du planning, apps/server/UxV2Skeleton.js : mêmes
 * fichiers, métriques connues d'avance) : on le garde tel quel. En redéclarer un du même nom le ferait recharger, et
 * le texte passerait un instant sur la police d'origine (1 à 2 px plus haut). Métriques différentes : on déclare.
 */
function hasEquivalentAlias(alias: string, overrides: { ascentOverride: string; descentOverride: string }): boolean {
  try {
    let found = false;
    let different = false;
    document.fonts.forEach((face) => {
      if (unquote(face.family) !== alias) return;
      if (samePercent(face.ascentOverride, overrides.ascentOverride) && samePercent(face.descentOverride, overrides.descentOverride)) {
        found = true;
      } else {
        different = true;
      }
    });
    return found && !different;
  } catch {
    return false;
  }
}

/** Alias d'une famille web (nom de l'alias) ; null si inutile ou impossible. Une seule fois par famille. */
function aliasFor(family: string, rules: CSSFontFaceRule[], ctx: CanvasRenderingContext2D): Promise<string | null> {
  const key = family.toLowerCase();
  const cached = aliasCache.get(key);
  if (cached) return cached;
  const task = (async () => {
    const weight = referenceWeight(rules);
    const loaded = await withTimeout(document.fonts.load(`normal ${weight} 16px "${family}"`, 'H'), LOAD_TIMEOUT_MS);
    if (!loaded || !document.fonts.check(`normal ${weight} 16px "${family}"`, 'H')) return null;
    const metrics = measure(family, weight, ctx);
    const overrides = metrics ? centeredOverrides(metrics) : null;
    if (!overrides) return null;
    const alias = aliasFamilyName(family);
    if (hasEquivalentAlias(alias, overrides)) return alias;
    rules.forEach((rule) => {
      const src = rule.style.getPropertyValue('src');
      if (!src) return;
      const descriptors: FontFaceDescriptors = {
        display: 'swap',
        ascentOverride: overrides.ascentOverride,
        descentOverride: overrides.descentOverride,
        // Pas d'interligne propre à la police : `line-height: normal` = zone de contenu, centrée sur le cœur du texte.
        // Avec l'interligne d'Helvetica Neue LT (2 à 3 px), Chromium arrondit la demi-interligne à l'entier inférieur :
        // texte 0,5 px trop haut à 12,5, 13 et 13,5 px. Les contrôles d'une ligne posent `line-height: normal`.
        lineGapOverride: '0%',
      };
      DESCRIPTORS.forEach(([key, property]) => {
        const value = rule.style.getPropertyValue(property).trim();
        if (value) (descriptors as Record<string, string>)[key] = value;
      });
      const base = rule.parentStyleSheet?.href || document.baseURI;
      document.fonts.add(new FontFace(alias, absolutizeFontSrc(src, base), descriptors));
    });
    return alias;
  })().catch(() => null);
  aliasCache.set(key, task);
  return task;
}

/** Pile corrigée (alias en tête) ; la pile telle quelle si rien n'est à corriger. Ne lève jamais. */
export async function alignFontStack(stack: string): Promise<string> {
  try {
    if (typeof document === 'undefined' || !document.fonts || typeof FontFace === 'undefined') return stack;
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return stack;
    for (const family of parseFontStack(stack)) {
      if (isGenericFamily(family)) return stack;
      const rules = findFontFaceRules(family);
      if (rules.length) {
        const alias = await aliasFor(family, rules, ctx);
        return alias ? stackWithAlias(alias, stack, family) : stack;
      }
      if (isLocalFontAvailable(family, ctx)) return stack;
    }
    return stack;
  } catch {
    return stack;
  }
}

/**
 * Le moteur applique-t-il ascent-override / descent-override ? Pas Safari (macOS, iOS, iPadOS) dans ses versions
 * publiées (caniuse, octobre 2026 ; seule la Technology Preview les a, comme le WebKit 26.6 de Playwright) : l'alias y
 * garde les métriques d'origine et le texte reste 0,147 em trop haut. Constaté sur la capture d'iPhone de Ségolène du
 * 13/10 : « Dynamo », « RIISE », « Punch » et « RÉSERVER » 2,3 px au-dessus du centre de leur pilule, l'écart d'origine
 * d'Helvetica Neue LT. Les descripteurs de @font-face échappent à @supports : détection par l'API FontFace.
 */
export function supportsMetricOverrides(): boolean {
  try {
    return typeof FontFace !== 'undefined' && 'ascentOverride' in FontFace.prototype;
  } catch {
    return false;
  }
}

/**
 * Repli des moteurs sans descripteurs de métriques : décalage vers le bas (em) qui centre le cœur du texte de la pile
 * dans sa boîte ; null si la police est déjà centrée, générique, installée sur l'appareil, ou illisible. Même famille
 * et même mesure que l'alias (aliasFor). Posé par mountV2 sur #mindbody-app (--mmv2-text-nudge, data-mmv2-nudge) et lu
 * par les contrôles d'une ligne (styles/ux-v2.css). Ne lève jamais.
 */
export async function textNudgeFor(stack: string): Promise<number | null> {
  try {
    if (typeof document === 'undefined' || !document.fonts) return null;
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) return null;
    for (const family of parseFontStack(stack)) {
      if (isGenericFamily(family)) return null;
      const rules = findFontFaceRules(family);
      if (rules.length) {
        const weight = referenceWeight(rules);
        const loaded = await withTimeout(document.fonts.load(`normal ${weight} 16px "${family}"`, 'H'), LOAD_TIMEOUT_MS);
        if (!loaded || !document.fonts.check(`normal ${weight} 16px "${family}"`, 'H')) return null;
        const metrics = measure(family, weight, ctx);
        return metrics ? textNudge(metrics) : null;
      }
      if (isLocalFontAvailable(family, ctx)) return null;
    }
    return null;
  } catch {
    return null;
  }
}

export interface FontStacks {
  font: string;
  fontDisplay: string;
}

/** Les deux piles de la v2, corrigées ; null si aucune ne change. */
export async function alignFontStacks(stacks: FontStacks): Promise<FontStacks | null> {
  const [font, fontDisplay] = await Promise.all([alignFontStack(stacks.font), alignFontStack(stacks.fontDisplay)]);
  return font === stacks.font && fontDisplay === stacks.fontDisplay ? null : { font, fontDisplay };
}
